import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Button, CircularProgress, Dialog, DialogContent, IconButton, Typography, Radio,
} from '@mui/material';
import CloseIcon from '@material-ui/icons/Close';
import CheckIcon from '@material-ui/icons/Check';

import { DQ_PRO_PLAN_KEY_VERBOSE, DQ_PRO_PLAN_KEY, TOAST_TYPES } from '../../../../utils/constants';
import { toast } from '../../../../utils/utils';
import { resolveSolutions, SOLUTION } from '../../../../config/workspaceNav/solutions';
import {
    unlockViewFor,
    UNLOCK_SUBMITTED_COPY,
} from '../constants/unlockCapabilities.config';
import { isClientWorkspace, isDirectWorkspace } from '../../../../utils/capabilities';
import {
    createUnlockRequestService, getUnlockOptionsService,
    createPartnerRequestService, getPartnerRequestOptionsService,
} from '../../../../services/client.service';
import { getProductPermissions, isProductEnabled } from '../../../../utils/productAccess';

const ORANGE = 'var(--brand-primary, #FF6900)';
// Neutral fills from the slides (Current access box, held row, Upgrade chip),
// kept in the current palette rather than the deck's greys.
const SUBTLE_BG = 'rgba(17, 24, 39, 0.03)';
const CHIP_BG = 'rgba(17, 24, 39, 0.06)';
const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

/**
 * What each school / network row needs on the org's own product access to
 * count as held — the same grants the API makes on approval
 * (UNLOCK_CAPABILITY_CATALOG). COS Readiness is held at its default variant,
 * Provider-Created (assessment + LearnLinker).
 */
const CLIENT_HELD_RULES = {
    [DQ_PRO_PLAN_KEY.ENTERPRISE_P0]: { cpc_index_assessment: ['view', 'create', 'link'] },
    [DQ_PRO_PLAN_KEY.ENTERPRISE_LC]: { learning_program: ['view', 'create', 'link'] },
    [DQ_PRO_PLAN_KEY.ENTERPRISE_P1]: { assessment: ['view', 'create', 'link'], learning_program: ['view', 'link'] },
};

/**
 * Does this school / network hold the row's capability? Read off the session's
 * `product_access`, which the API resolves from the ORG's own overrides for a
 * client user — not off the plan, which on a client session is the partner's.
 */
const clientHolds = (user, planKey) => {
    const rule = CLIENT_HELD_RULES[planKey];
    if (!rule) return false;
    return Object.entries(rule).every(([product, dims]) => {
        if (!isProductEnabled(user, product)) return false;
        const perms = getProductPermissions(user, product);
        return dims.every((d) => perms[d] === true);
    });
};

/** "10 Aug 2026" — the date format on the Request Submitted panel. */
const formatSubmitted = (d) => {
    const when = d ? new Date(d) : new Date();
    if (Number.isNaN(when.getTime())) return '';
    return when.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

/**
 * "Unlock a capability" — the flow behind every locked step card on Home.
 *
 * Partner view (PW deck slides 6 / 8 / 9 / 10): pick a capability, Continue
 * or Cancel. A partner's request goes to DQ (Gap Register G005): Continue
 * sends POST partner-request/create and the dialog turns into the same
 * "Request Submitted" panel — the deck draws no form in between (slide 9:
 * "check and continue: Popup + System Email"). A DQ administrator approves or
 * declines it in the admin portal. Each row's key — the plan key of a solution
 * upgrade, the feature key of the rest — is the request type. GET
 * partner-request/options marks rows already requested.
 *
 * Not built: slide 10's payment gate after a solution upgrade ("Complete
 * Payment to Activate Access"). The request waits for the admin instead.
 *
 * `initialSelected` opens the dialog with a row already picked — the COS
 * Government Checklist page's Request Access (BTN-0018) does this; `source`
 * records where it was opened from.
 *
 * School / School Network view (SW deck slides 6-8, SNW deck slides 5-7): three
 * SOLUTION UPGRADES, reviewed by the PARTNER. This one is wired:
 *   · each row is marked "Active" from the org's own product access — the
 *     session's product_access (clientHolds) at once, then GET
 *     unlock-request/options, which also knows what is already requested;
 *   · Continue sends POST unlock-request/create — the request lands in the
 *     partner's Opportunity Pipeline — and the dialog turns into the
 *     "Request Submitted" panel; Done closes it.
 *
 * Layout follows SW-P0-UnlockRequest: Current access in its own filled box,
 * a monospace section label, a held row tinted with a plain tick, and a footer
 * of a wide Continue beside Cancel. Colours stay in the current palette.
 *
 * A direct workspace (a school buying for itself) has no partner to ask, so it
 * keeps the unwired behaviour.
 */
const UnlockCapabilityDialog = ({ open, onClose, authUser, initialSelected = null, source = 'unlock_dialog' }) => {
    const [selected, setSelected] = useState(null);
    useEffect(() => { if (open && initialSelected) setSelected(initialSelected); }, [open, initialSelected]);

    const isClient = isClientWorkspace(authUser);
    // A school or network with a partner behind it — the only caller the API accepts.
    const sendsToPartner = isClient && !isDirectWorkspace(authUser);
    // A partner: its requests go to DQ.
    const sendsToDq = !isClient && !isDirectWorkspace(authUser);

    /*
     * A school and a school network get a different picker from the partner:
     * SOLUTION UPGRADES only, and their request is reviewed by the partner
     * rather than by DQ. See unlockViewFor for the slides.
     */
    const { sections: UNLOCK_SECTIONS, copy: UNLOCK_DIALOG_COPY } = unlockViewFor(isClient);

    // ── School / network: the org's own state, from the API ─────────────────
    const [options, setOptions] = useState(null);   // { current_access, options: [{ key, state }] }
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(null); // { name, createdAt }

    // ── Partner: its own requests to DQ ─────────────────────────────────────
    const [dqOptions, setDqOptions] = useState(null); // { can_request, types }

    const loadDqOptions = () => getPartnerRequestOptionsService()
        .then((res) => setDqOptions(res?.data || null))
        .catch(() => setDqOptions(null));

    useEffect(() => {
        if (!open || !sendsToDq) return;
        loadDqOptions();
    }, [open, sendsToDq]); // eslint-disable-line react-hooks/exhaustive-deps

    const dqTypes = useMemo(() => Object.fromEntries(
        (dqOptions?.types || []).map((t) => [t.key, t]),
    ), [dqOptions]);

    useEffect(() => {
        if (!open || !sendsToPartner) return undefined;
        let cancelled = false;
        getUnlockOptionsService()
            .then((res) => { if (!cancelled) setOptions(res?.data || null); })
            // The session's own access below still marks the rows.
            .catch(() => { if (!cancelled) setOptions(null); });
        return () => { cancelled = true; };
    }, [open, sendsToPartner]);

    const optionState = useMemo(() => Object.fromEntries(
        (options?.options || []).map((o) => [o.key, o.state]),
    ), [options]);

    /*
     * The school's held rows straight from its session, so the dialog is right
     * the moment it opens and stays right if the options call fails. The API's
     * answer, when it arrives, wins — it also knows what is already requested.
     */
    const sessionHeld = (planKey) => clientHolds(authUser, planKey);
    const allRows = UNLOCK_SECTIONS.flatMap((s) => s.rows);

    const currentPlanKey = authUser?.dq_pro_plan?.plan_key;
    const currentAccess = sendsToPartner
        ? (options?.current_access
            || allRows.filter((r) => sessionHeld(r.planKey)).map((r) => r.name).join(', ')
            || '—')
        : (DQ_PRO_PLAN_KEY_VERBOSE[currentPlanKey] || '—');

    /**
     * What the reader already has, per the three PW UnlockRequest slides.
     *
     * An exact plan-key match is not enough. Slide 7 (PW-P1) shows THREE rows
     * already held on one plan — COS Children Assessment "Active", LearnCreator
     * "Included", COS Readiness Solution "Active" — because the P1 package
     * grants all three. Comparing planKey to the current plan marked only one
     * of them and left the other two looking purchasable.
     *
     * So this reads the SOLUTIONS the plan grants (PLAN_TO_SOLUTIONS, the same
     * map the sidebar gates on) rather than the plan's name:
     *
     *   P0  → COS Children Assessment active            (slide 6)
     *   P1  → all three active; LearnCreator "Included" (slide 7)
     *   LC  → LearnCreator active                       (slide 8)
     *
     * A school or network with a partner reads its own access instead: its
     * session carries the PARTNER's plan, which says nothing about what the
     * school itself has been given.
     */
    const heldSolutions = resolveSolutions(authUser) || [];
    const has = (sol) => heldSolutions.includes(sol);

    const rowState = (row) => {
        // A partner's request already with DQ, or approved.
        const dqState = sendsToDq ? dqTypes[row.planKey || row.featureKey]?.state : null;
        if (dqState === 'requested') return 'requested';
        if (dqState === 'approved' && row.featureKey) return 'active';
        if (sendsToPartner) {
            const state = options ? optionState[row.planKey] : (sessionHeld(row.planKey) ? 'active' : 'open');
            if (state === 'active') return 'active';
            if (state === 'requested') return 'requested';
            return 'open';
        }
        if (row.planKey === DQ_PRO_PLAN_KEY.ENTERPRISE_P0) {
            // Slide 7: on P1 the assessment is Active as well — the Readiness
            // Solution is assessment AND learning, "connect assessment to learning
            // and back again", so a P1 reader already has it.
            return (has(SOLUTION.P0) || has(SOLUTION.P1)) ? 'active' : 'open';
        }
        if (row.planKey === DQ_PRO_PLAN_KEY.ENTERPRISE_LC) {
            if (has(SOLUTION.LC)) return 'active';
            // Slide 7: on a P1 package LearnCreator is not bought separately —
            // "Included in your current COS Readiness Solution."
            if (has(SOLUTION.P1)) return 'included';
            return 'open';
        }
        if (row.planKey === DQ_PRO_PLAN_KEY.ENTERPRISE_P1
            || row.planKey === DQ_PRO_PLAN_KEY.ENTERPRISE_P1A
            || row.planKey === DQ_PRO_PLAN_KEY.ENTERPRISE_P1B) {
            return has(SOLUTION.P1) ? 'active' : 'open';
        }
        return 'open';
    };

    /** Held either outright or by inclusion — both render ticked and unselectable. */
    const isActive = (row) => rowState(row) === 'active' || rowState(row) === 'included';

    const selectedRow = useMemo(
        () => allRows.find((r) => (r.planKey || r.featureKey) === selected),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selected, UNLOCK_SECTIONS],
    );

    const close = () => {
        setSelected(null);
        setSubmitted(null);
        onClose();
    };

    const handleContinue = async () => {
        if (sendsToDq && selectedRow) {
            if (dqOptions && dqOptions.can_request === false) {
                toast(TOAST_TYPES.INFO, 'Ask your workspace owner', 'Only the workspace owner can send requests to DQ.');
                return;
            }
            await sendToDq();
            return;
        }
        if (!sendsToPartner) {
            close();
            return;
        }
        if (!selectedRow || submitting) return;
        setSubmitting(true);
        try {
            const res = await createUnlockRequestService(selectedRow.planKey);
            if (res && res.data) {
                setSubmitted({ name: selectedRow.name, createdAt: res.data.request?.createdAt });
                setSelected(null);
            }
        } catch {
            /* invokeApi toasts the reason (already requested, already active, …) */
        } finally {
            setSubmitting(false);
        }
    };

    /** A partner's request — straight to DQ, no form (PW deck slide 9). */
    const sendToDq = async () => {
        if (!selectedRow || submitting) return;
        setSubmitting(true);
        try {
            const res = await createPartnerRequestService({
                request_type: selectedRow.planKey || selectedRow.featureKey,
                source,
            });
            if (res && res.data) {
                setSubmitted({ name: selectedRow.name, createdAt: res.data.request?.requested_at });
                setSelected(null);
                loadDqOptions();
            }
        } catch {
            /* invokeApi toasts the reason (already requested, …) */
        } finally {
            setSubmitting(false);
        }
    };

    const Row = ({ row }) => {
        const id = row.planKey || row.featureKey;
        const state = rowState(row);
        const active = isActive(row);
        const requested = state === 'requested';
        const selectable = row.selectable !== false && !active && !requested;
        const chosen = selected === id;
        const badge = active
            ? (UNLOCK_DIALOG_COPY.activeBadge || 'Already active')
            // Not drawn on the slides: an ask already with the partner. Shown so a
            // second click does not end in a "you have already requested" error.
            : requested ? 'Requested' : row.badge;
        // "Active" is the held chip (tinted); "Upgrade" and the rest are neutral.
        const badgeTinted = active || requested;

        return (
            <Box
                onClick={() => selectable && setSelected(id)}
                sx={{
                    display: 'flex', alignItems: 'flex-start', gap: 1.5,
                    border: `${chosen ? 2 : 1}px solid ${chosen ? ORANGE : 'var(--brand-border, #E5E7EB)'}`,
                    borderRadius: '12px', p: chosen ? 1.875 : 2, mb: 1.25,
                    cursor: selectable ? 'pointer' : 'default',
                    // A held row is shown, not dimmed (slide: tinted row, muted name).
                    opacity: selectable || active || requested ? 1 : 0.6,
                    backgroundColor: chosen ? 'rgba(255,105,0,0.08)' : (active ? SUBTLE_BG : 'transparent'),
                    transition: 'border-color 120ms, background-color 120ms',
                    '&:hover': selectable ? { borderColor: ORANGE } : {},
                }}
            >
                {active
                    ? <CheckIcon style={{ color: ORANGE, fontSize: 18, marginTop: 2 }}/>
                    : (
                        <Radio
                            checked={chosen}
                            disabled={!selectable}
                            size="small"
                            sx={{ p: 0, mt: '2px', color: 'var(--brand-border, #D1D5DB)', '&.Mui-checked': { color: ORANGE } }}
                        />
                    )}
                <Box sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography sx={{
                            fontSize: '0.95rem', fontWeight: active ? 600 : 700,
                            color: active ? 'var(--brand-text-muted, #6B7280)' : 'var(--brand-text, #111827)',
                        }}>
                            {row.name}
                        </Typography>
                        {badge && (
                            <Typography sx={{
                                fontSize: '0.72rem', fontWeight: 500,
                                color: badgeTinted ? ORANGE : 'var(--brand-text-muted, #6B7280)',
                                backgroundColor: badgeTinted ? 'rgba(255,105,0,0.1)' : CHIP_BG,
                                borderRadius: '6px', px: 0.875, py: '1px',
                            }}>
                                {badge}
                            </Typography>
                        )}
                    </Box>
                    <Typography sx={{ fontSize: '0.85rem', color: 'var(--brand-text-muted, #6B7280)', mt: 0.5 }}>
                        {row.desc}
                    </Typography>
                </Box>
            </Box>
        );
    };

    // Slide footer: a wide Continue on the left, Cancel on the right, both
    // rounded rectangles.
    const secondaryButtonSx = {
        textTransform: 'none', fontWeight: 600, fontSize: '14px',
        color: 'var(--brand-text, #111827)',
        border: '1px solid var(--brand-border, #E5E7EB)',
        borderRadius: '10px', px: 3.5, py: 1.1,
        '&:hover': { borderColor: ORANGE, color: ORANGE, backgroundColor: 'rgba(255,105,0,0.04)' },
    };
    const primaryButtonSx = {
        textTransform: 'none', fontWeight: 600, fontSize: '14px',
        backgroundColor: ORANGE, color: '#fff',
        borderRadius: '10px', px: 2.5, py: 1.1, boxShadow: 'none',
        '&:hover': { backgroundColor: '#e55a00', boxShadow: 'none' },
        '&.Mui-disabled': { backgroundColor: CHIP_BG, color: 'var(--brand-text-muted, #9CA3AF)' },
    };

    /*
     * Request Submitted — SW / SNW-P0, LC-UnlockRequestResponse. The slide
     * strikes out the Request and Status rows; only Submitted is kept.
     */
    const submittedView = submitted && (
        <Box sx={{ textAlign: 'center', pt: 1 }}>
            <Box sx={{
                width: 44, height: 44, borderRadius: '50%', mx: 'auto', mb: 2,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundColor: 'rgba(255,105,0,0.12)',
            }}>
                <CheckIcon style={{ color: ORANGE, fontSize: 24 }}/>
            </Box>
            <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--brand-text, #111827)', mb: 1 }}>
                {UNLOCK_SUBMITTED_COPY.title}
            </Typography>
            <Typography sx={{ fontSize: '0.9rem', color: 'var(--brand-text, #111827)', mb: 1.5 }}>
                {UNLOCK_SUBMITTED_COPY.body(submitted.name)}
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', color: 'var(--brand-text-muted, #6B7280)', mb: 2.5 }}>
                {UNLOCK_SUBMITTED_COPY.note}
            </Typography>
            <Box sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                borderTop: '1px solid var(--brand-border, #E5E7EB)',
                borderBottom: '1px solid var(--brand-border, #E5E7EB)',
                py: 1.25, mb: 2.5,
            }}>
                <Typography sx={{ fontSize: '0.85rem', color: 'var(--brand-text-muted, #6B7280)' }}>
                    {UNLOCK_SUBMITTED_COPY.submittedLabel}
                </Typography>
                <Typography sx={{ fontSize: '0.85rem', color: 'var(--brand-text, #111827)', fontFamily: MONO }}>
                    {formatSubmitted(submitted.createdAt)}
                </Typography>
            </Box>
            <Button variant="contained" fullWidth onClick={close} sx={primaryButtonSx}>
                {UNLOCK_SUBMITTED_COPY.doneCta}
            </Button>
        </Box>
    );

    return (
        <Dialog
            open={open}
            onClose={close}
            fullWidth
            PaperProps={{ sx: { maxWidth: 480, borderRadius: '20px', backgroundColor: 'var(--brand-surface, #fff)' } }}
        >
            <DialogContent sx={{ p: 3 }}>
                {submittedView || (
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                            <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--brand-text, #111827)' }}>
                                {UNLOCK_DIALOG_COPY.title}
                            </Typography>
                            <IconButton size="small" onClick={close} aria-label="Close"
                                sx={{ color: 'var(--brand-text-muted, #9CA3AF)' }}>
                                <CloseIcon style={{ fontSize: 18 }}/>
                            </IconButton>
                        </Box>

                        <Typography sx={{ fontSize: '0.9rem', color: 'var(--brand-text, #374151)', mb: 2 }}>
                            {UNLOCK_DIALOG_COPY.subtitle}
                        </Typography>

                        {/* Current access — its own filled box on the slide, label left, value beside it. */}
                        <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 4,
                            backgroundColor: SUBTLE_BG,
                            border: '1px solid var(--brand-border, #E5E7EB)',
                            borderRadius: '12px', px: 2, py: 1.5, mb: 2.5,
                        }}>
                            <Typography sx={{ fontSize: '0.75rem', color: 'var(--brand-text-muted, #6B7280)', flexShrink: 0 }}>
                                {UNLOCK_DIALOG_COPY.currentAccessLabel}
                            </Typography>
                            <Typography sx={{ fontSize: '0.9rem', color: 'var(--brand-text, #111827)' }}>
                                {currentAccess}
                            </Typography>
                        </Box>

                        {UNLOCK_SECTIONS.map((section) => (
                            <Box key={section.heading} sx={{ mb: 2 }}>
                                <Typography sx={{
                                    fontFamily: MONO, fontSize: '0.7rem', fontWeight: 400, letterSpacing: '1px',
                                    color: 'var(--brand-text-muted, #9CA3AF)', mb: 1,
                                }}>
                                    {section.heading}
                                </Typography>
                                {section.rows.map((row) => <Row key={row.planKey || row.featureKey} row={row}/>)}
                            </Box>
                        ))}

                        {UNLOCK_DIALOG_COPY.footnote && (
                            <Typography sx={{
                                fontSize: '0.8rem', color: 'var(--brand-text-muted, #6B7280)',
                                backgroundColor: 'var(--brand-bg, #FFFBF7)',
                                borderLeft: `3px solid ${ORANGE}`,
                                borderRadius: '8px', p: 1.5, mb: 2.5,
                            }}>
                                {UNLOCK_DIALOG_COPY.footnote}
                            </Typography>
                        )}

                        <Box sx={{
                            display: 'flex', gap: 1.5,
                            borderTop: '1px solid var(--brand-border, #E5E7EB)', pt: 2.5, mt: 1,
                        }}>
                            <Button
                                variant="contained"
                                disabled={!selected || submitting}
                                onClick={handleContinue}
                                sx={{ ...primaryButtonSx, flex: 1 }}
                            >
                                {submitting
                                    ? <CircularProgress size={16} sx={{ color: '#fff' }}/>
                                    : UNLOCK_DIALOG_COPY.confirmCta}
                            </Button>
                            <Button onClick={close} sx={secondaryButtonSx}>
                                {UNLOCK_DIALOG_COPY.cancelCta}
                            </Button>
                        </Box>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default UnlockCapabilityDialog;
